// src/components/dashboard/widgets/google-reviews-extended-widget.tsx
import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';

interface ReviewItem { rating: number | null; text: string; authorName: string; publishTime: string | null }
interface ReviewsData { rating: number | null; reviewCount: number | null; reviews: ReviewItem[] }

interface GoogleReviewsExtendedWidgetProps {
  venueId: string;
}

function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 1) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function GoogleReviewsExtendedWidget({ venueId }: GoogleReviewsExtendedWidgetProps) {
  const c = useAppColors();
  const [data, setData] = useState<ReviewsData | null | undefined>(undefined);

  useEffect(() => {
    supabase.functions.invoke('venue-reviews', { body: { action: 'get', venue_id: venueId } }).then(({ data: d }) => {
      setData(d?.ok ? { rating: d.rating, reviewCount: d.reviewCount, reviews: d.reviews ?? [] } : null);
    });
  }, [venueId]);

  return (
    <div className="rounded-2xl border p-5 bg-warm-gray border-leaf-500/20 w-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-leaf-500/10 flex items-center justify-center">
          <Star size={15} className="text-leaf-400" />
        </div>
        <span className="text-sm font-semibold text-cream">
          {data?.rating != null ? `${data.rating.toFixed(1)}★` : '—'}
        </span>
        <span className="text-xs text-cream-muted">{data?.reviewCount?.toLocaleString() ?? 0} reviews</span>
      </div>

      {data === undefined ? (
        <div className="text-xs text-cream-muted/50">Loading…</div>
      ) : !data || data.reviews.length === 0 ? (
        <div className="text-xs text-cream-muted/50">No reviews found yet</div>
      ) : (
        <div className="space-y-2.5">
          {data.reviews.map((r, i) => (
            <div key={i} className="rounded-lg p-3" style={{ background: c.iconBg, border: `1px solid ${c.border}` }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-cream">{r.authorName}</span>
                <span className="text-[10px]" style={{ color: c.textFaint }}>{r.rating}★ · {timeAgo(r.publishTime)}</span>
              </div>
              <p className="text-[11px] text-cream-muted leading-relaxed">{r.text || 'No comment left.'}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}